import axios from 'axios';

const API = axios.create({ baseURL: '/api' });

// Display price — informational only. Server enforces the real value via env
// (STRIPE_PRICE_PER_RESUME_CENTS); this constant is just for the button label.
// Keep in sync with backend default in api/src/billing/billing.service.ts.
export const PRICE_PER_RESUME_CENTS = 149;

export interface ResumeInfo {
  name: string;
  type: string;
  textLength: number;
}

export interface ParsedResume {
  name: string;
  type: string;
  text: string;
}

export interface UploadResult {
  jd: { name: string; preview: string } | null;
  jdText: string | null;
  resumes: ResumeInfo[];
  resumesFull: ParsedResume[];
  warnings: string[];
  skippedJds: string[];
}

export interface CandidateEvaluation {
  name: string;
  evaluation: string;
}

export interface ScreeningResult {
  finalOutput: string;
  evaluations: CandidateEvaluation[];
  csvContent: string | null;
  candidateCount: number;
  agentName: string;
}

export interface CheckoutResponse {
  checkoutUrl: string;
  sessionId: string;
  amountCents: number;
  count: number;
}

export interface VerifyResponse {
  paid: boolean;
  count: number;
  consumed: boolean;
}

// ─── screening ──────────────────────────────────────────────────────

export async function uploadFiles(files: File[]): Promise<UploadResult> {
  const formData = new FormData();
  files.forEach((f) => formData.append('files', f));
  const res = await API.post<UploadResult>('/screening/upload', formData);
  return res.data;
}

export async function runScreening(
  sessionId: string,
  jdText: string,
  resumes: ParsedResume[],
  existingEvaluations?: CandidateEvaluation[],
  customInstructions?: string,
): Promise<ScreeningResult> {
  const res = await API.post<ScreeningResult>('/screening/screen', {
    sessionId,
    jdText,
    resumes,
    existingEvaluations,
    customInstructions,
  });
  return res.data;
}

// ─── billing ────────────────────────────────────────────────────────

export async function createCheckout(count: number): Promise<CheckoutResponse> {
  const res = await API.post<CheckoutResponse>('/billing/checkout', { count });
  return res.data;
}

export async function verifySession(
  sessionId: string,
): Promise<VerifyResponse> {
  const res = await API.post<VerifyResponse>('/billing/verify', { sessionId });
  return res.data;
}
